import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, AlertCircle, Package, Search, CheckSquare } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TRCAdmin = () => {
  const [devices, setDevices] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [selectedEngineer, setSelectedEngineer] = useState('');
  const [priority, setPriority] = useState('normal');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('rfr');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadDevices();
    loadEngineers();
  }, [filterStatus]);

  const loadDevices = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/trc/devices`;
      
      if (filterStatus === 'rfr') {
        url = `${API_URL}/api/trc/devices/rfr/list`;
      } else if (filterStatus === 'unassigned') {
        url = `${API_URL}/api/trc/devices?status=binned,iqc,ntf,tf_pna`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Filter out devices that already have engineers if looking for unassigned
        let deviceList = response.data.devices;
        if (filterStatus === 'unassigned') {
          deviceList = deviceList.filter(d => !d.assigned_engineer);
        }
        setDevices(deviceList);
      }
    } catch (error) {
      console.error('Error loading devices:', error);
    }
  };

  const loadEngineers = async () => {
    try {
      const token = localStorage.getItem('token');
      // Get all users with Engineer role
      const response = await axios.get(
        `${API_URL}/api/employees`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data) {
        // Filter engineers
        const engineersList = response.data.filter(e => 
          e.role === 'Engineer' || e.employee_id?.startsWith('ENG')
        );
        setEngineers(engineersList);
      }
    } catch (error) {
      console.error('Error loading engineers:', error);
      // Mock engineers if API fails
      setEngineers([
        { employee_id: 'ENG001', name: 'John Engineer', role: 'Engineer' },
        { employee_id: 'ENG002', name: 'Sarah Tech', role: 'Engineer' },
        { employee_id: 'ENG003', name: 'Mike Repair', role: 'Engineer' }
      ]);
    }
  };

  const toggleDeviceSelection = (deviceId) => {
    if (selectedDevices.includes(deviceId)) {
      setSelectedDevices(selectedDevices.filter(id => id !== deviceId));
    } else {
      setSelectedDevices([...selectedDevices, deviceId]);
    }
  };

  const selectAll = () => {
    if (selectedDevices.length === filteredDevices.length) {
      setSelectedDevices([]);
    } else {
      setSelectedDevices(filteredDevices.map(d => d.trc_device_id));
    }
  };

  const handleAssign = async () => {
    if (selectedDevices.length === 0) {
      alert('Please select at least one device');
      return;
    }
    if (!selectedEngineer) {
      alert('Please select an engineer');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/trc/engineer/assign`,
        {
          device_ids: selectedDevices,
          engineer_id: selectedEngineer,
          priority: priority
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert(`Successfully assigned ${selectedDevices.length} devices to engineer!`);
        setSelectedDevices([]);
        setSelectedEngineer('');
        loadDevices();
      }
    } catch (error) {
      console.error('Error assigning devices:', error);
      alert(error.response?.data?.detail || 'Error assigning devices');
    } finally {
      setLoading(false);
    }
  };

  const filteredDevices = devices.filter(d =>
    d.did?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.trc_device_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Users className="w-8 h-8 text-indigo-600" />
          <h1 className="text-3xl font-bold text-gray-900">TRC Admin - Device Assignment</h1>
        </div>

        {/* Assignment Panel */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Assign Devices to Engineer</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Select Engineer */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Select Engineer *</label>
              <select
                value={selectedEngineer}
                onChange={(e) => setSelectedEngineer(e.target.value)}
                className="w-full px-4 py-3 border-2 rounded-lg"
              >
                <option value="">-- Select Engineer --</option>
                {engineers.map(eng => (
                  <option key={eng.employee_id} value={eng.employee_id}>
                    {eng.name} ({eng.employee_id})
                  </option>
                ))}
              </select>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full px-4 py-3 border-2 rounded-lg"
              >
                <option value="normal">Normal</option>
                <option value="sos">SOS Priority</option>
              </select>
            </div>

            {/* Assign Button */}
            <div className="flex items-end">
              <button
                onClick={handleAssign}
                disabled={loading || selectedDevices.length === 0 || !selectedEngineer}
                className="w-full py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:bg-gray-300"
              >
                {loading ? 'Assigning...' : `Assign ${selectedDevices.length} Device(s)`}
              </button>
            </div>
          </div>

          {selectedDevices.length > 0 && (
            <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
              <p className="text-sm text-indigo-900">
                <strong>{selectedDevices.length} device(s) selected</strong> - Ready to assign to engineer
              </p>
            </div>
          )}
        </div>

        {/* Filters & Search */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by DID, brand, model..."
                className="w-full pl-10 pr-4 py-3 border-2 rounded-lg"
              />
            </div>

            {/* Status Filter */}
            <div className="flex gap-3">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="flex-1 px-4 py-3 border-2 rounded-lg"
              >
                <option value="rfr">Ready for Repair (RFR)</option>
                <option value="unassigned">Unassigned Devices</option>
                <option value="all">All Devices</option>
              </select>

              <button
                onClick={selectAll}
                className="px-4 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
              >
                <CheckSquare className="w-5 h-5" />
                {selectedDevices.length === filteredDevices.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          </div>
        </div>

        {/* Devices List */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-xl font-bold text-gray-900">
              Available Devices ({filteredDevices.length})
            </h2>
          </div>

          {filteredDevices.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <Package className="w-16 h-16 mx-auto mb-3 text-gray-300" />
              <p>No devices found</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredDevices.map((device, index) => {
                const isSelected = selectedDevices.includes(device.trc_device_id);
                const daysInTRC = Math.floor(
                  (new Date() - new Date(device.created_at)) / (1000 * 60 * 60 * 24)
                );

                return (
                  <div
                    key={index}
                    onClick={() => toggleDeviceSelection(device.trc_device_id)}
                    className={`p-4 cursor-pointer transition-colors ${
                      isSelected ? 'bg-indigo-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className="w-5 h-5 text-indigo-600"
                      />

                      {/* Device Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-gray-900">{device.did}</span>
                          {device.is_sos && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs font-semibold rounded">
                              SOS
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">
                          {device.brand} {device.model} | {device.category}
                        </p>
                        <p className="text-xs text-gray-500">
                          TRC ID: {device.trc_device_id}
                        </p>
                      </div>

                      {/* Status & Metrics */}
                      <div className="text-right">
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-semibold">
                          {device.status}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          Days in TRC: {daysInTRC}
                        </p>
                        {device.assigned_engineer && (
                          <p className="text-xs text-orange-600 mt-1">
                            Assigned: {device.assigned_engineer}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Engineer Workload Summary */}
        <div className="mt-6 bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Engineer Workload Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {engineers.map(eng => (
              <div key={eng.employee_id} className="p-4 border-2 rounded-lg">
                <div className="font-bold text-gray-900">{eng.name}</div>
                <div className="text-sm text-gray-600">{eng.employee_id}</div>
                <div className="mt-2 text-sm">
                  <span className="text-gray-600">Current Load:</span>
                  <span className="ml-2 font-semibold">
                    {devices.filter(d => d.assigned_engineer === eng.employee_id).length} / 25
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TRCAdmin;
